import { useState, useEffect } from "react";
import { useI18n } from "../lib/i18n.jsx";
import { functions } from "../lib/firebase.js";
import { httpsCallable } from "firebase/functions";

export default function PublishTeam({ user, onBack }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: "", slug: "",
    description_en: "", description_es: "", description_pt: "",
    short_desc_en: "", short_desc_es: "", short_desc_pt: "",
    category: "", tags: "", price: "0",
    repoUrl: "", tag: "", screenshots: "",
    ownerName: user?.displayName || user?.email || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // GitHub state
  const [githubConnected, setGitHubConnected] = useState(false);
  const [githubLogin, setGitHubLogin] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [repos, setRepos] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedRepo, setSelectedRepo] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);

  // Listen for OAuth result from popup
  useEffect(() => {
    const interval = setInterval(async () => {
      const stored = localStorage.getItem("github-oauth-code");
      if (!stored) return;
      try {
        const data = JSON.parse(stored);
        if (!data.code || !data.timestamp) return;
        if (Date.now() - data.timestamp > 120000) {
          localStorage.removeItem("github-oauth-code");
          return;
        }
        localStorage.removeItem("github-oauth-code");

        const fn = httpsCallable(functions, "exchangeGithubCode");
        const result = await fn({ code: data.code, spaceId: "global" });
        const res = result.data;
        if (res.ok) {
          setGitHubConnected(true);
          setGitHubLogin(res.githubLogin);
          loadOrgs();
        } else {
          setError("Failed to connect GitHub");
        }
      } catch (e) {
        console.error("OAuth error:", e);
        setError(e.message);
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  async function loadOrgs() {
    setLoadingOrgs(true);
    try {
      const fn = httpsCallable(functions, "listGitHubOrgs");
      const res = await fn({});
      setOrgs(res.data.orgs);
    } catch (e) { console.error("Orgs error:", e); }
    setLoadingOrgs(false);
  }

  async function handleOrgChange(org) {
    setSelectedOrg(org);
    setSelectedRepo("");
    setSelectedTag("");
    setRepos([]);
    setTags([]);
    if (!org) return;
    setLoadingRepos(true);
    try {
      const fn = httpsCallable(functions, "listGitHubRepos");
      const res = await fn({ org });
      setRepos(res.data.repos);
    } catch (e) { console.error("Repos error:", e); }
    setLoadingRepos(false);
  }

  async function handleRepoChange(repoFullName) {
    setSelectedRepo(repoFullName);
    setSelectedTag("");
    setTags([]);
    const found = repos.find(r => r.fullName === repoFullName);
    if (found) {
      setForm(f => ({ ...f, repoUrl: found.url }));
      // Load tags for this repo via GitHub API
      loadTags(repoFullName, found.defaultBranch || "main");
    }
  }

  async function loadTags(repoFullName, defaultBranch) {
    setLoadingTags(true);
    try {
      const [owner, repo] = repoFullName.split("/");
      const res = await fetch(`https://api.github.com/repos/${owner}/${repo}/tags?per_page=50`);
      if (res.ok) {
        const tagData = await res.json();
        const tagNames = tagData.map(t => t.name);
        setTags(tagNames);
        // If no tags, use "main" as default
        if (tagNames.length === 0) {
          setTags([defaultBranch]);
          setSelectedTag(defaultBranch);
          setForm(f => ({ ...f, tag: defaultBranch }));
        }
      }
    } catch (e) { console.error("Tags error:", e); }
    setLoadingTags(false);
  }

  function handleTagChange(tag) {
    setSelectedTag(tag);
    setForm(f => ({ ...f, tag }));
  }

  function handleConnectGitHub() {
    localStorage.removeItem("github-oauth-code");
    const callbackUrl = `${window.location.origin}/oauth/callback`;
    window.open(
      `https://github.com/login/oauth/authorize?client_id=Iv23liqo7QtrQG9uV7iI&redirect_uri=${encodeURIComponent(callbackUrl)}&state=global`,
      "github-oauth", "width=600,height=700"
    );
  }

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const fn = httpsCallable(functions, "publishMarketplaceTeam");
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        screenshots: form.screenshots.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      await fn(payload);
      onBack();
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  }

  return (
    <div className="publish-team">
      <button className="btn btn-ghost back-btn" onClick={onBack}>← {t("back")}</button>
      <h2>{t("publish_team")}</h2>

      <form className="publish-form" onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-field">
            <label>{t("team_name")}</label>
            <input className="form-input" value={form.name} onChange={(e) => set("name", e.target.value)} required />
          </div>
          <div className="form-field">
            <label>{t("team_slug")}</label>
            <input className="form-input" value={form.slug} onChange={(e) => set("slug", e.target.value)} required />
          </div>
        </div>

        <div className="form-field">
          <label>{t("team_category")}</label>
          <input className="form-input" value={form.category} onChange={(e) => set("category", e.target.value)} required />
        </div>

        <div className="form-field">
          <label>{t("team_tags")}</label>
          <input className="form-input" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="analytics, data, automation" />
        </div>

        <div className="form-field">
          <label>{t("team_price")}</label>
          <input className="form-input" type="number" min="0" step="0.01" value={form.price}
                 onChange={(e) => set("price", e.target.value)} />
        </div>

        {/* GitHub Connection */}
        <div className="form-section">
          <h3>GitHub Repository</h3>
          {!githubConnected ? (
            <button type="button" className="btn btn-github" onClick={handleConnectGitHub}>
              <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor"><path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>
              Connect GitHub
            </button>
          ) : (
            <p style={{ color: "var(--accent)", fontSize: 13, marginBottom: 12 }}>
              ✅ Connected as <strong>{githubLogin}</strong>
            </p>
          )}

          {githubConnected && (
            <>
              <div className="form-field">
                <label>Organization</label>
                <select className="form-select" value={selectedOrg}
                  onChange={(e) => handleOrgChange(e.target.value)}>
                  <option value="">{loadingOrgs ? "Loading..." : "Select organization..."}</option>
                  {orgs.map((o) => (
                    <option key={o.login} value={o.login}>{o.login}</option>
                  ))}
                </select>
              </div>

              {selectedOrg && (
                <div className="form-field">
                  <label>Repository</label>
                  <select className="form-select" value={selectedRepo}
                    onChange={(e) => handleRepoChange(e.target.value)}>
                    <option value="">{loadingRepos ? "Loading..." : "Select repository..."}</option>
                    {repos.map((r) => (
                      <option key={r.fullName} value={r.fullName}>{r.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedRepo && (
                <div className="form-field">
                  <label>{t("team_tag")}</label>
                  <select className="form-select" value={selectedTag}
                    onChange={(e) => handleTagChange(e.target.value)}>
                    <option value="">{loadingTags ? "Loading..." : "Select tag (default: latest commit)"}</option>
                    {tags.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
        </div>

        <div className="form-section">
          <h3>English</h3>
          <div className="form-field">
            <label>{t("team_short_desc")}</label>
            <input className="form-input" value={form.short_desc_en} onChange={(e) => set("short_desc_en", e.target.value)} />
          </div>
          <div className="form-field">
            <label>{t("team_description")}</label>
            <textarea className="form-textarea" rows={4} value={form.description_en}
                      onChange={(e) => set("description_en", e.target.value)} />
          </div>
        </div>

        <div className="form-section">
          <h3>Español</h3>
          <div className="form-field">
            <label>{t("team_short_desc")}</label>
            <input className="form-input" value={form.short_desc_es} onChange={(e) => set("short_desc_es", e.target.value)} />
          </div>
          <div className="form-field">
            <label>{t("team_description")}</label>
            <textarea className="form-textarea" rows={4} value={form.description_es}
                      onChange={(e) => set("description_es", e.target.value)} />
          </div>
        </div>

        <div className="form-section">
          <h3>Português</h3>
          <div className="form-field">
            <label>{t("team_short_desc")}</label>
            <input className="form-input" value={form.short_desc_pt} onChange={(e) => set("short_desc_pt", e.target.value)} />
          </div>
          <div className="form-field">
            <label>{t("team_description")}</label>
            <textarea className="form-textarea" rows={4} value={form.description_pt}
                      onChange={(e) => set("description_pt", e.target.value)} />
          </div>
        </div>

        <div className="form-field">
          <label>{t("team_screenshots")}</label>
          <textarea className="form-textarea" rows={3} value={form.screenshots}
                    onChange={(e) => set("screenshots", e.target.value)} />
        </div>

        {error && <div className="form-error">{error}</div>}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? t("loading") : t("save")}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onBack}>{t("cancel")}</button>
        </div>
      </form>
    </div>
  );
}
